import { z } from 'zod';
import { TxType } from './tx-type';

/**
 * 집계 조회 API의 계약 (17회차, 콘솔 검토 C-2·B절 #10).
 *
 * 16회차까지 서버의 조회는 전부 `SELECT *`였다. `duckdb.service.ts` 전체에 `GROUP BY`가
 * 워커 1분 집계 하나뿐이라 "어느 진입점이 느린가", "어느 SQL이 오래 도는가"를 화면에서 볼
 * 방법이 없었다. 제니퍼 대비 가장 큰 공백이라 적혀 있던 자리다.
 *
 * 여기 있는 것은 **집계 결과의 모양**이다. 저장 파일의 행(`TxSummary`·`SpanRecord`·
 * `WorkerAggregate`)과 달리 파일로 남지 않고 조회할 때마다 만들어진다. 기존 조회의 응답 봉투는
 * `responses.ts`에 있다 — 그쪽은 이미 있던 API를 계약으로 옮긴 것이고, 이 파일은 새 API다.
 *
 * `node:*` import를 넣지 않는다. 콘솔이 이 파일을 소스째 번들한다.
 *
 * 26회차부터 집계의 묶는 기준에 **`service`가 들어간다.** 앱이 둘이면 같은 경로·같은 시각도
 * 서비스마다 줄이 나뉜다. `?service=`를 주면 그 서비스 줄만 나오므로 25회차까지와 같은 모양이다.
 * 두 앱의 같은 경로가 한 줄로 합쳐지면 그 줄의 p95가 무엇의 p95인지 말할 수 없다.
 */

const count = z.number().int().nonnegative();
const ms = z.number().nonnegative();
const epochMs = z.number().int().nonnegative();

/**
 * 집계를 인스턴스로 나누는 스위치 (30회차). `?split=instance`.
 *
 * 서비스는 26회차부터 언제나 묶는 기준이지만 인스턴스는 **달라고 할 때만** 나눈다. 인스턴스는
 * 재시작·배포마다 이름이 바뀌고 같은 앱의 프로세스라 기본 표에서는 합쳐 보는 것이 맞다.
 * "이 느린 줄이 어느 프로세스에서 나왔나"를 볼 때 켠다.
 */
export const STATS_SPLITS = ['instance'] as const;
export const StatsSplit = z.enum(STATS_SPLITS);
export type StatsSplit = z.infer<typeof StatsSplit>;

/**
 * 시간대별 추이가 받는 나누기 (30회차). 추이는 다른 집계와 달리 **기본이 안 나눔**이다 —
 * 한 그래프에 점은 버킷마다 하나여야 하고, p50·p95는 나눈 뒤에 합칠 수 없다.
 * `service`는 26회차의 서비스별 점(같은 시각에 서비스마다 한 점), `instance`는 그 아래까지다.
 */
export const TIMELINE_SPLITS = ['service', 'instance'] as const;
export const TimelineSplit = z.enum(TIMELINE_SPLITS);
export type TimelineSplit = z.infer<typeof TimelineSplit>;

/** 집계가 여는 날짜 폴더 정보. `responses.ts`의 조회와 같은 뜻이다 */
const scanned = {
  /** 이번 조회가 연 날짜 폴더 수 */
  days: count,
  /**
   * 다 못 봤다. 날짜 폴더 상한(감사 D-2)·파일 수 상한, 또는 **행 상한**에 걸린 경우다
   * (리뷰 R26-10. 추이·워커 합계는 서비스 수만큼 행이 늘어 자체 상한이 있다).
   * 참이면 `total`도 본 범위 안의 값이다
   */
  truncated: z.boolean().optional(),
};

// ---- 진입점별 통계 ----

/** `GET /api/stats/entries`의 정렬 기준 */
export const ENTRY_STATS_ORDERS = ['count', 'p95', 'max', 'errorRate'] as const;
export const EntryStatsOrder = z.enum(ENTRY_STATS_ORDERS);
export type EntryStatsOrder = z.infer<typeof EntryStatsOrder>;

/**
 * 진입점 하나의 통계 (콘솔 검토 C-2).
 *
 * 묶는 기준은 `entry`와 `txType` 둘이다. 계획서는 `entry` 하나였는데, 같은 경로가 두 타입으로
 * 잡히면(예: `/stream` 경로가 한동안 http로 분류되던 구간이 섞이면) 평균과 p95가 뜻을 잃는다.
 * 실제로는 진입점 하나에 타입 하나라 행 수가 늘지 않고, 어긋난 경우에만 두 줄로 보인다.
 */
export const EntryStats = z.object({
  /**
   * 어느 서비스의 줄인지 (26회차). 묶는 기준에 서비스가 들어가므로 서비스가 둘이면 같은
   * 진입점이라도 줄이 나뉜다. `?service=`를 주면 그 서비스 한 줄만 나온다.
   * 26회차 전 응답에는 없던 칸이라 선택이다
   */
  service: z.string().optional(),
  /**
   * 어느 인스턴스의 줄인지 (30회차). `?split=instance`를 줬을 때만 묶는 기준에 들어가 채워진다.
   * 안 주면 인스턴스를 합친 줄이라 비어 있다. `?instance=`로 좁히면 그 값 하나다
   */
  instance: z.string().optional(),
  /** 진입점. http는 "METHOD /route", worker는 워커 이름 */
  entry: z.string().min(1),
  txType: TxType,
  /** 이 기간의 호출 수 */
  count,
  /**
   * 오류로 끝난 건수. 판정은 화면·검색과 같은 `@apm/core`의 3단계 중 `error`다
   * (`status <> 'ok'` 또는 5xx). 4xx는 여기 안 들어온다
   */
  errorCount: count,
  /** `errorCount / count`. 0~1 */
  errorRate: z.number().min(0).max(1),
  p50Ms: ms,
  p95Ms: ms,
  maxMs: ms,
  /** 요청 한 건이 돌린 SQL 평균 건수. N+1을 이 값으로 먼저 찾는다 */
  avgSqlCount: z.number().nonnegative(),
});
export type EntryStats = z.infer<typeof EntryStats>;

/** `GET /api/stats/entries` 응답 */
export const EntryStatsResult = z.object({
  rows: z.array(EntryStats),
  /** 조건에 맞는 진입점 수. `rows`는 상한에 잘렸을 수 있다 */
  total: count,
  /** 이 기간의 전체 호출 수. 상한에 잘린 진입점까지 포함한다 */
  totalCount: count,
  /** sse를 넣고 셌는지. 기본은 빼고 센다 (2026-09-07 결정 4) */
  includeSse: z.boolean(),
  orderBy: EntryStatsOrder,
  ...scanned,
});
export type EntryStatsResult = z.infer<typeof EntryStatsResult>;

// ---- 느린 SQL ----

/** `GET /api/stats/sql`의 정렬 기준 */
export const SQL_STATS_ORDERS = ['total', 'p95', 'count', 'max'] as const;
export const SqlStatsOrder = z.enum(SQL_STATS_ORDERS);
export type SqlStatsOrder = z.infer<typeof SqlStatsOrder>;

/**
 * SQL 문장 하나의 통계 (콘솔 검토 B절 #10, 24회차 감사 D-26).
 *
 * 묶는 기준은 **리터럴을 지운 문장**이다. 에이전트는 2026-09-07 결정 7로 리터럴을 그대로
 * 보내므로(`apm.sql_params`와 같은 태도) 문장 그대로 묶으면 `IN (1,2,3)`과 `IN (4,5)`,
 * `LIMIT 20`과 `LIMIT 50`이 서로 다른 줄이 된다. 서버가 집계할 때만 `@apm/core`의
 * `sqlGroupKey`(DuckDB에는 `apm_sql_key`로 등록)로 키를 만든다. 저장 프로시저 호출은
 * 인자 수와 무관하게 이름으로 접는다(29회차).
 *
 * `query`는 그 그룹에서 **가장 오래 걸린 한 건의 원문**이다. 리터럴이 살아 있어
 * `slowestTraceId`를 눌러 들어간 요청에서 같은 문장을 그대로 찾을 수 있다.
 */
export const SqlStats = z.object({
  /**
   * 어느 서비스의 문장인지 (26회차). 묶는 기준에 서비스가 들어가므로 같은 문장이라도
   * 서비스가 다르면 다른 줄이다. 26회차 전에 저장한 span 파일에는 서비스가 없어 그 줄에는 없다
   */
  service: z.string().optional(),
  /**
   * 어느 인스턴스의 줄인지 (30회차). `?split=instance`를 줬을 때만 묶는 기준에 들어가 채워진다.
   * 안 주면 인스턴스를 합친 줄이라 비어 있다. `?instance=`로 좁히면 그 값 하나다. 26회차 전 span 파일에는 인스턴스가 없어 그 줄은 비어 있다
   */
  instance: z.string().optional(),
  /** SQL 문장. 가장 오래 걸린 한 건의 원문이라 리터럴이 그대로 있다 */
  query: z.string().min(1),
  /**
   * 이 줄로 묶인 서로 다른 문장 수 (24회차). 리터럴만 다른 것을 몇 종 접었는지다.
   * 옛 응답에는 없어 1로 읽는다
   */
  variants: count.default(1),
  /** 이 기간에 돈 횟수 */
  count,
  /** 소요 시간 합계. "무엇이 DB 시간을 가장 많이 먹는가"는 이 값으로 본다 */
  totalMs: ms,
  p95Ms: ms,
  maxMs: ms,
  /** 실패한 건수 */
  errorCount: count,
  /** 가장 오래 걸린 한 건의 traceId. 눌러서 그 요청 상세로 간다 */
  slowestTraceId: z.string().regex(/^[0-9a-f]{32}$/),
});
export type SqlStats = z.infer<typeof SqlStats>;

/** `GET /api/stats/sql` 응답 */
export const SqlStatsResult = z.object({
  rows: z.array(SqlStats),
  /** 조건에 맞는 문장 수. `rows`는 상한에 잘렸을 수 있다 */
  total: count,
  /** 이 기간에 돈 SQL 전체 건수. 상한에 잘린 문장까지 포함한다 */
  totalCount: count,
  /**
   * 실제로 자료가 있는 시작 시각.
   *
   * span 보존 기간은 요약(tx)보다 짧다(`coldSpanDays` ≠ `coldTxDays`). "지난 30일"을 물어도
   * 답은 최근 며칠분일 수 있다. 화면이 "이 기간 전체를 본 것이 아니다"를 말할 수 있게
   * 조건에 맞은 행 중 가장 이른 시각을 함께 준다. 자료가 없으면 비어 있다
   */
  coveredFrom: epochMs.optional(),
  /**
   * 트랜잭션 제어 문장(START TRANSACTION·BEGIN·COMMIT·ROLLBACK·SAVEPOINT·RELEASE)이라
   * 통계에서 뺀 건수 (22-B회차, 2026-09-08 사용자 확정 4). 판정은 `@apm/core`의
   * `isTxControlSql`과 같다. 화면이 몇 건을 뺐는지 적는다
   */
  excludedTxControl: count,
  /**
   * 세션 설정 문장(`SET`·`RESET`·`SHOW`·`SELECT set_config(`·`SELECT current_setting(`)이라
   * 통계에서 뺀 건수 (23-A회차, 2026-09-08 사용자 확정 22). 판정은 `@apm/core`의
   * `isSessionSql`과 같다. 트랜잭션 제어와 따로 세는 이유는 뺀 까닭이 달라서다
   */
  excludedSession: count,
  orderBy: SqlStatsOrder,
  ...scanned,
});
export type SqlStatsResult = z.infer<typeof SqlStatsResult>;

// ---- 외부 호출 ----

/**
 * `GET /api/stats/external`의 정렬 기준 (37회차). 진입점 통계와 같은 넷이다.
 *
 * 이름이 `external.ts`의 **외부 연동**(밖에 내놓는 API)과 닮았지만 다른 것이다.
 * 이쪽은 **우리 앱이 밖으로 부른 호출**을 대상별로 센 것이다.
 */
export const EXTERNAL_STATS_ORDERS = ['count', 'p95', 'max', 'errorRate'] as const;
export const ExternalStatsOrder = z.enum(EXTERNAL_STATS_ORDERS);
export type ExternalStatsOrder = z.infer<typeof ExternalStatsOrder>;

/**
 * 나가는 호출 대상 하나의 통계 (37회차).
 *
 * 요청 안의 외부 HTTP 호출(OneWorks2의 Gemini·firebase·MCP 루프백, PLM의 llama.cpp,
 * MES의 `HttpWebRequest`)은 25회차부터 span으로 저장돼 있었지만 **어느 대상이 얼마나
 * 느린지·오류인지를 한눈에 보는 표가 없었다.** 느린 SQL과 같은 자리에 둔다.
 *
 * 묶는 기준은 **대상의 authority**(`host` 또는 `host:port`)다. 저장된 span의 `target`은
 * `host/path`라 경로까지 묶으면 id가 든 경로마다 줄이 생겨 표가 터진다. 루프백(MCP 자기
 * 재호출)은 대상이 `127.0.0.1:<포트>`로 보이는 것이 사실이라 그대로 둔다.
 */
export const ExternalStats = z.object({
  /**
   * 어느 서비스가 부른 것인지 (26회차 규칙). 묶는 기준에 서비스가 들어가므로 앱이 둘이면
   * 같은 대상이라도 줄이 나뉜다. 26회차 전에 저장한 span 파일에는 서비스가 없어 그 줄에는 없다
   */
  service: z.string().optional(),
  /**
   * 어느 인스턴스가 부른 것인지 (30회차 규칙). `?split=instance`를 줬을 때만 묶는 기준에
   * 들어가 채워진다. 안 주면 인스턴스를 합친 줄이라 비어 있다
   */
  instance: z.string().optional(),
  /** 부른 상대. `host` 또는 `host:port`다. 경로와 쿼리는 뗀 값이다 */
  target: z.string().min(1),
  /** 이 기간에 그 대상을 부른 횟수 */
  count,
  /** 실패한 건수. span의 종료 상태가 `error`인 것이다 */
  errorCount: count,
  /** `errorCount / count`. 0~1 */
  errorRate: z.number().min(0).max(1),
  /** 호출 한 건의 평균 소요 시간 */
  avgMs: ms,
  p95Ms: ms,
  maxMs: ms,
  /** 가장 최근 호출의 시작 시각. "아직 부르고 있는 대상인가"를 이 값으로 본다 */
  lastAt: epochMs,
});
export type ExternalStats = z.infer<typeof ExternalStats>;

/** `GET /api/stats/external` 응답 */
export const ExternalStatsResult = z.object({
  rows: z.array(ExternalStats),
  /** 조건에 맞는 대상 수. `rows`는 상한에 잘렸을 수 있다 */
  total: count,
  /** 이 기간의 나가는 호출 전체 건수. 상한에 잘린 대상까지 포함한다 */
  totalCount: count,
  /**
   * 실제로 자료가 있는 시작 시각. 느린 SQL과 같은 뜻이다 — span 보존 기간이 요약보다
   * 짧아 "지난 30일"을 물어도 답은 최근 며칠분일 수 있다. 자료가 없으면 비어 있다
   */
  coveredFrom: epochMs.optional(),
  orderBy: ExternalStatsOrder,
  ...scanned,
});
export type ExternalStatsResult = z.infer<typeof ExternalStatsResult>;

// ---- 워커 추이 ----

/** 워커 추이의 버킷 하나. `WorkerAggregate`를 인스턴스까지 합친 것이다 */
export const WorkerTrendPoint = z.object({
  /** 1분 버킷의 시작 시각(epoch ms) */
  bucketStart: epochMs,
  /**
   * 어느 서비스의 줄인지 (26회차). 묶는 기준에 서비스가 들어가므로 서비스가 둘이면 같은
   * 진입점이라도 줄이 나뉜다. `?service=`를 주면 그 서비스 한 줄만 나온다.
   * 26회차 전 응답에는 없던 칸이라 선택이다
   */
  service: z.string().optional(),
  /**
   * 어느 인스턴스의 줄인지 (30회차). `?split=instance`를 줬을 때만 묶는 기준에 들어가 채워진다.
   * 안 주면 인스턴스를 합친 줄이라 비어 있다. `?instance=`로 좁히면 그 값 하나다
   */
  instance: z.string().optional(),
  entry: z.string().min(1),
  count,
  totalMs: ms,
  maxMs: ms,
  errorCount: count,
  sqlCount: count,
});
export type WorkerTrendPoint = z.infer<typeof WorkerTrendPoint>;

/** 진입점 하나의 기간 합계 */
export const WorkerTrendTotal = z.object({
  /**
   * 어느 서비스의 줄인지 (26회차). 묶는 기준에 서비스가 들어가므로 서비스가 둘이면 같은
   * 진입점이라도 줄이 나뉜다. `?service=`를 주면 그 서비스 한 줄만 나온다.
   * 26회차 전 응답에는 없던 칸이라 선택이다
   */
  service: z.string().optional(),
  /**
   * 어느 인스턴스의 줄인지 (30회차). `?split=instance`를 줬을 때만 묶는 기준에 들어가 채워진다.
   * 안 주면 인스턴스를 합친 줄이라 비어 있다. `?instance=`로 좁히면 그 값 하나다
   */
  instance: z.string().optional(),
  entry: z.string().min(1),
  count,
  totalMs: ms,
  /** `totalMs / count` */
  avgMs: ms,
  maxMs: ms,
  errorCount: count,
  sqlCount: count,
});
export type WorkerTrendTotal = z.infer<typeof WorkerTrendTotal>;

/**
 * `GET /api/stats/workers` 응답 (17회차, 15회차 저장 위에).
 *
 * `GET /api/workers`가 버킷 원본을 그대로 준다면 이쪽은 **화면이 그릴 모양**이다.
 * 인스턴스를 합쳐 진입점별 시계열(`points`)과 기간 합계(`totals`)를 함께 준다.
 * 워커는 HTTP 진입이 없어 `TxSummary` 행이 아예 없다. 이 API가 워커를 보는 유일한 길이다
 * (2026-09-07 결정 1).
 */
export const WorkerTrendResult = z.object({
  /** 시각 오름차순, 같은 시각은 진입점 이름순 */
  points: z.array(WorkerTrendPoint),
  /** 건수 내림차순 */
  totals: z.array(WorkerTrendTotal),
  /** 버킷 폭(ms). 저장이 1분 단위라 지금은 항상 60000이다 */
  bucketMs: z.number().int().positive(),
  ...scanned,
});
export type WorkerTrendResult = z.infer<typeof WorkerTrendResult>;

// ---- 시간대별 추이 ----

/** `GET /api/stats/timeline`이 받는 버킷 폭(ms). 1분·5분·1시간 (22-B회차, 빠진 정보 1) */
export const TIMELINE_BUCKETS_MS = [60_000, 300_000, 3_600_000] as const;

/** 한 시간대의 요청 수·오류 수·응답시간 */
export const TimelinePoint = z.object({
  /** 버킷의 시작 시각(epoch ms). `floor(startedAt / bucketMs) * bucketMs` */
  bucketStart: epochMs,
  /**
   * 어느 서비스의 점인지. **30회차부터 `?split=service`(또는 `instance`)를 줬을 때만** 묶는
   * 기준에 들어가 채워지고, `?service=`로 좁혔으면 그 값이 실린다. 둘 다 아니면 서비스를
   * 합친 점이라 비어 있다. 26회차에는 언제나 나뉘었는데, 점을 그리는 화면이 버킷마다 한
   * 점을 기대해 서비스가 둘이면 한쪽 값이 다른 쪽을 덮어썼다
   */
  service: z.string().optional(),
  /** 어느 인스턴스의 점인지 (30회차). `?split=instance`거나 `?instance=`로 좁혔을 때만 */
  instance: z.string().optional(),

  count,
  /** 오류로 끝난 건수. 판정은 진입점 통계와 같은 `error` 단계다 */
  errorCount: count,
  p50Ms: ms,
  p95Ms: ms,
  maxMs: ms,
});
export type TimelinePoint = z.infer<typeof TimelinePoint>;

/**
 * `GET /api/stats/timeline` 응답 (22-B회차, 빠진 정보 1).
 *
 * 요청 수·p95·오류율의 시간대별 선 그래프가 이것으로 그려진다. 요청이 없던 버킷은 오지 않는다.
 * 화면이 0으로 채운다. 오류율은 `errorCount / count`로 화면이 낸다.
 */
export const TimelineResult = z.object({
  /** 시각 오름차순 */
  points: z.array(TimelinePoint),
  bucketMs: z.number().int().positive(),
  /** 실제로 센 기간. 화면이 축을 이 값으로 잡는다 */
  from: epochMs,
  to: epochMs,
  /** sse를 넣고 셌는지. 진입점 통계와 같은 뜻이다 */
  includeSse: z.boolean(),
  ...scanned,
});
export type TimelineResult = z.infer<typeof TimelineResult>;

// ---- 같은 오류 묶기 ----

/**
 * 같은 오류 묶음 하나 (22-B회차, 빠진 정보 3).
 *
 * 묶는 기준은 심각도·예외 종류·상태 코드·DB 오류 코드·진입점·타입이다. 어느 하나라도 다르면
 * 다른 원인일 수 있어 따로 센다. 대표 건(`lastTraceId`)은 가장 최근 것이다. 원인을 볼 때는
 * 지금 나는 것이 중요하다.
 */
export const ErrorGroup = z.object({
  /**
   * 어느 서비스의 줄인지 (26회차). 묶는 기준에 서비스가 들어가므로 서비스가 둘이면 같은
   * 진입점이라도 줄이 나뉜다. `?service=`를 주면 그 서비스 한 줄만 나온다.
   * 26회차 전 응답에는 없던 칸이라 선택이다
   */
  service: z.string().optional(),
  /** 화면·검색과 같은 3단계 중 `error`(5xx·예외)와 `fail`(4xx·DB 오류) */
  level: z.enum(['error', 'fail']),
  errorType: z.string().optional(),
  httpStatus: z.number().int().min(100).max(599).optional(),
  sqlErrorCode: z.string().optional(),
  entry: z.string().min(1),
  txType: TxType,
  count,
  firstAt: epochMs,
  lastAt: epochMs,
  /** 가장 최근 건의 traceId. 눌러서 상세로 간다 */
  lastTraceId: z.string().regex(/^[0-9a-f]{32}$/),
  /** 가장 최근 건의 예외 메시지. 묶음의 대표 문장으로 보인다 */
  lastMessage: z.string().optional(),
  /**
   * 가장 최근 건의 스택 첫 프레임 (25회차). 서버 오류(5xx)에만 있다.
   * 묶음 목록에서 "어느 줄에서 난 오류인가"를 상세로 들어가지 않고 보기 위해 한 줄 둔다
   */
  lastFrame: z.string().max(200).optional(),
});
export type ErrorGroup = z.infer<typeof ErrorGroup>;

/** `GET /api/stats/errors` 응답. 건수 내림차순, 같으면 최근 것부터 */
export const ErrorGroupsResult = z.object({
  rows: z.array(ErrorGroup),
  /** 묶음 수. `rows`는 상한에 잘렸을 수 있다 */
  total: count,
  /** 오류·실패로 끝난 요청 전체 건수. 상한에 잘린 묶음까지 포함한다 */
  totalCount: count,
  /** 그중 `error` 단계 건수. 나머지가 `fail`이다 */
  errorCount: count,
  ...scanned,
});
export type ErrorGroupsResult = z.infer<typeof ErrorGroupsResult>;
